import React, { useState } from 'react';
import './styles.scss';
import { getFirstLetterOfEachString } from '@common/text-helpers';
import { getValue } from '@utils/lodash';
import { convertCurrentDate } from '@common/date-helpers';
import { updateComment } from '@services/comments.service';

interface RejectDetailsAccordionProps {
  reject: any;
  type?: 'rejected' | 'sent_back';
  refreshData?: () => void;
}

const RejectDetailsAccordion = ({
  reject,
  type = 'rejected',
  refreshData,
}: RejectDetailsAccordionProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedComment, setEditedComment] = useState(reject?.comments || '');
  const [isSaving, setIsSaving] = useState(false);

  const toggleAccordion = () => setIsOpen(!isOpen);

  const handleEdit = () => {
    setIsEditing(true);
    setEditedComment(reject?.comments || '');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedComment(reject?.comments || '');
  };

  const handleSave = async () => {
    if (!editedComment.trim()) return;
    setIsSaving(true);
    try {
      await updateComment(reject.id, { comments: editedComment });
      setIsEditing(false);
      if (refreshData) {
        refreshData();
      } else {
        // Fallback: manually update the UI if refreshData is not provided
        reject.comments = editedComment;
      }
    } catch (error) {
      console.error('Failed to update comment', error);
    } finally {
      setIsSaving(false);
    }
  };

  const getLabel = () => {
    switch (type) {
      case 'sent_back':
        return 'Reason for sent back';
      case 'rejected':
      default:
        return 'Reason for rejection';
    }
  };

  const getActionText = () => {
    switch (type) {
      case 'sent_back':
        return 'Sent Back By';
      case 'rejected':
      default:
        return 'Rejected By';
    }
  };

  const getActionDate = () => {
    switch (type) {
      case 'sent_back':
        return 'Sent back on';
      case 'rejected':
      default:
        return 'Rejected on';
    }
  };

  const labelText = getLabel();

  return (
    <div className="single-accordion">
      <button
        className={`accordion-header ${isOpen ? 'active' : ''}`}
        onClick={toggleAccordion}
      >
        <div className="left">
          <img src="/images/alert-triangle.svg" alt="Warning" />
          <p className=".header_text__20">{labelText}</p>
        </div>
        <img
          src="/images/arrow-down.svg"
          className={`img-fluid dropdown-arrow cursor-pointer ${
            isOpen ? 'rotated' : ''
          }`}
          alt="Toggle"
        />
      </button>

      <div className={`accordion-body ${isOpen ? 'open' : ''}`}>
        <div className="accordion-content row-layout">
          <div className="comment-section p-3">
            <div className="d-flex justify-content-between align-items-center">
              <p className="comment-label">Comments :</p>
              {!isEditing &&
                reject?.id &&
                getValue(localStorage, 'view', '') !== 'client' && (
                  <img
                    src="/images/edit-gray.svg"
                    alt="Edit"
                    className="cursor-pointer"
                    onClick={handleEdit}
                    style={{ width: '16px', height: '16px' }}
                  />
                )}
            </div>
            {isEditing ? (
              <div className="mt-2">
                <textarea
                  className="form-control"
                  value={editedComment}
                  onChange={(e) => setEditedComment(e.target.value)}
                  disabled={isSaving}
                  rows={3}
                />
                <div className="d-flex justify-content-end mt-2">
                  <button
                    className="btn btn-sm btn-outline-secondary mr-2"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleSave}
                    disabled={isSaving || !editedComment.trim()}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <p className="comment-text">{reject?.comments || '—'}</p>
            )}
          </div>
          <div className="rejected-by p-3 border-left">
            <p className="trip-mandate-approval-info">{getActionText()}</p>

            <div className="d-flex align-items-center trip-mandate-profile-wrap">
              <div className="profile_circle bg-success">
                <h5 className="title">
                  {getFirstLetterOfEachString(
                    getValue(reject?.created_by, `first_name`, ''),
                  )}
                </h5>
              </div>
              <div>
                <h6>
                  {getValue(reject?.created_by, `first_name`, '')}{' '}
                  {getValue(reject?.created_by, `middle_name`, '')}{' '}
                  {getValue(reject?.created_by, `last_name`, '')}
                </h6>
                <p className="small_text">
                  {getValue(reject?.created_by, `email`, '')}
                </p>
                <p className="small_text">
                  {getActionDate()} : {convertCurrentDate(reject?.created_at)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RejectDetailsAccordion;
